import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  EmailRequestType,
  LabelId,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import {
  useCommunicationIntentsStore,
  useInboxEntriesStore,
  useUserCommunicationsStore,
} from '../../../store';
import { getLabelIdsForAction } from '../../../utils/emailLabelHelpers';
import { getCurrentTabParam } from '../../../utils/getCurrentTabParam';
import { navigateToInboxOrDoBox } from '../../../utils/navigation';
import { useCustomToast } from '../../useCustomToast';
import {
  applyOptimisticEntryLabelHide,
  revertOptimisticEntryLabelHide,
} from '../shared';
import { useCancelSnoozeOnRemoval } from '../snooze';

export const useReportAsSpamUserCommunication = () => {
  const { t } = useTranslation();
  const { cancelSnoozeFor } = useCancelSnoozeOnRemoval();

  const { errorMessage } = useCustomToast();
  const navigate = useNavigate();

  const handleReportAsSpamUserCommunication = useCallback(
    async (
      messageIds: string | string[],
      options?: { shouldNavigate?: boolean },
    ) => {
      const {
        setDeleteLoading,
        updateUserCommunicationLabels,
        getUserCommunicationByMessageId,
      } = useUserCommunicationsStore.getState();
      const { setLabelsIntent, clearIntent, settleIntent } =
        useCommunicationIntentsStore.getState();
      const {
        removeUserCommunicationsFromInboxEntries,
        removeInboxEntry,
        getCommunicationsByInboxEntryId,
        updateUserCommunicationLabelsInInboxEntries,
      } = useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      const communicationsToSpam = messageIdsArray
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        );

      if (communicationsToSpam.length === 0) {
        errorMessage(t('inbox.userCommunication.actions.reportAsSpamError'));
        return;
      }

      const linkedEntryIds = Array.from(
        new Set(
          communicationsToSpam.flatMap(
            ({ communication }) => communication.inboxEntryIds ?? [],
          ),
        ),
      );

      const entryIdsToRemove = linkedEntryIds.filter((entryId) => {
        const currentCommunications = getCommunicationsByInboxEntryId(entryId);
        const remainingCommunications = (currentCommunications || []).filter(
          (comm) =>
            !communicationsToSpam.some((c) => c.messageId === comm.messageId),
        );
        return remainingCommunications.length === 0;
      });

      setDeleteLoading(true);

      const labelBackupMap = new Map<string, LabelId[]>();

      communicationsToSpam.forEach(({ messageId, communication }) => {
        labelBackupMap.set(messageId, communication.labelIds || []);
        const expectedLabelIds = getLabelIdsForAction(
          communication.labelIds,
          'spam',
        );

        setLabelsIntent({
          messageId,
          threadMessageId: communication.threadMessageId,
          internetMessageId: communication.internetMessageId,
          originalLabelIds: communication.labelIds,
          expectedLabelIds,
        });
        updateUserCommunicationLabels(messageId, expectedLabelIds);
      });

      applyOptimisticEntryLabelHide(
        updateUserCommunicationLabelsInInboxEntries,
        communicationsToSpam.map(({ messageId }) => messageId),
        'spam',
      );

      const revertOptimisticSpam = (messageIdsToRevert: string[]) => {
        messageIdsToRevert.forEach((messageId) => {
          const previousLabels = labelBackupMap.get(messageId);
          if (previousLabels) {
            updateUserCommunicationLabels(messageId, previousLabels);
          }
          clearIntent(messageId);
        });
        revertOptimisticEntryLabelHide(
          updateUserCommunicationLabelsInInboxEntries,
          messageIdsToRevert,
          labelBackupMap,
        );
      };

      try {
        const operations = communicationsToSpam.map(
          async ({ messageId, communication }) => {
            try {
              if (!communication.userEmailAddress) {
                throw new Error('No user email address found in communication');
              }

              if (!communication.platform) {
                throw new Error('No platform found in communication');
              }

              await modifyEmail({
                userEmailAddress: communication.userEmailAddress,
                platform: communication.platform,
                messageId: communication.messageId,
                action: EmailRequestType.SPAM,
              });

              settleIntent(messageId);

              return { messageId, success: true, communication };
            } catch (error) {
              log.error(
                `Failed to report email as spam for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error, communication };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length === 0) {
          log.info(
            `${communicationsToSpam.length} messages reported as spam successfully`,
          );

          removeUserCommunicationsFromInboxEntries(
            communicationsToSpam.map((comm) => comm.messageId),
          );

          cancelSnoozeFor(
            communicationsToSpam.map(({ messageId }) => messageId),
          );

          if (entryIdsToRemove.length > 0) {
            await Promise.all(
              entryIdsToRemove.map(async (entryId) => {
                await removeInboxEntry(entryId);
                log.info(
                  `Removed inbox entry ${entryId} as last message was reported as spam`,
                );
              }),
            );
          }

          if (options?.shouldNavigate) {
            const currentTab = getCurrentTabParam();
            navigateToInboxOrDoBox(navigate, currentTab);
          }
        } else {
          log.warn(
            `${failed.length} operations failed out of ${communicationsToSpam.length}`,
          );

          const failedMessageIds = failed.map((result) => result.messageId);
          revertOptimisticSpam(failedMessageIds);

          errorMessage(
            failed.length === communicationsToSpam.length
              ? t('inbox.userCommunication.actions.reportAsSpamError')
              : t('inbox.userCommunication.actions.partialReportAsSpamError', {
                  failed: failed.length,
                  total: communicationsToSpam.length,
                }),
          );
        }
      } catch (error) {
        log.error(error);
        revertOptimisticSpam(messageIdsArray);
        errorMessage(t('inbox.userCommunication.actions.reportAsSpamError'));
      } finally {
        setDeleteLoading(false);
      }
    },
    [errorMessage, t, navigate, cancelSnoozeFor],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleReportAsSpamUserCommunication,
    extractMessageIdFromSelectedItems,
  };
};
